import { call, put, takeEvery } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { authApi } from '../../api/authApi';
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  logoutRequest,
  logoutSuccess,
  tokenExpired,
  forgotPasswordRequest,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  verifyResetTokenRequest,
  verifyResetTokenSuccess,
  verifyResetTokenFailure,
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordFailure
} from '../actions/authActions';
import { setUser, clearUser } from '../../store/slices/userSlice';
import { clearAuthState } from '../../utils/tokenUtils';

function* loginSaga(action) {
  try {
    yield put(loginRequest());
    const response = yield call(authApi.login, action.payload);
    
    // Store tokens and user in sessionStorage
    sessionStorage.setItem('authToken', response.accessToken);
    if (response.refreshToken) {
      sessionStorage.setItem('refreshToken', response.refreshToken);
    }
    sessionStorage.setItem('user', JSON.stringify(response));
    
    yield put(loginSuccess(response, response.accessToken));
    yield put(setUser(response));
    toast.success(response?.message || 'Login successful', {
      toastId: "login-success"
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Login failed';
    yield put(loginFailure(errorMessage));
    toast.error(errorMessage, {
      toastId: "login-error"
    });
  }
}
function* logoutSaga() {
  try {
    yield put(logoutRequest());
    
    // Clear all auth state using utility function
    clearAuthState();
    
    // Try to call logout API but don't block on failure
    try {
      yield call(authApi.logout);
    } catch (apiError) {
      // API call failed but continue with logout
      console.warn('Logout API call failed:', apiError);
    }
    
    yield put(logoutSuccess());
    
    toast.success('Logged out successfully!');
    
    // Use a small delay to ensure state is cleared before navigation
    yield new Promise(resolve => setTimeout(resolve, 100));
    
    // Signal navigation to parent component instead of hard refresh
    window.dispatchEvent(new CustomEvent('logout-complete'));
  } catch (error) {
    // Even if logout fails, clear all state
    clearAuthState();
    yield put(logoutSuccess());
    
    toast.success('Logged out successfully!');
    
    // Signal navigation to parent component
    window.dispatchEvent(new CustomEvent('logout-complete'));
  }
}

function* handleTokenExpired() {
  // Clear all auth state using utility function
  clearAuthState();
  
  yield put(tokenExpired());
  
  toast.error('Session expired. Please login again.');
  
  // Signal navigation to parent component
  window.dispatchEvent(new CustomEvent('token-expired'));
}

function* forgotPasswordSaga(action) {
  try {
    yield put(forgotPasswordRequest());
    const response = yield call(authApi.forgotPassword, action.payload);
    yield put(forgotPasswordSuccess(response.message));
    toast.success(response.message || 'Password reset link sent to your email');
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to send reset link';
    yield put(forgotPasswordFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* verifyResetTokenSaga(action) {
  try {
    yield put(verifyResetTokenRequest());
    const { resetToken, email } = action.payload;
    yield call(authApi.verifyResetToken, resetToken, email);
    yield put(verifyResetTokenSuccess());
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Invalid or expired reset token';
    yield put(verifyResetTokenFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* resetPasswordSaga(action) {
  try {
    yield put(resetPasswordRequest());
    const { resetToken, email, newPassword } = action.payload;
    const response = yield call(authApi.resetPassword, resetToken, email, newPassword);
    yield put(resetPasswordSuccess(response.message));
    toast.success(response.message || 'Password reset successfully');
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password';
    yield put(resetPasswordFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* authSaga() {
  yield takeEvery('LOGIN_SAGA', loginSaga);
  yield takeEvery('LOGOUT_SAGA', logoutSaga);
  yield takeEvery('TOKEN_EXPIRED_SAGA', handleTokenExpired);
  yield takeEvery('FORGOT_PASSWORD_SAGA', forgotPasswordSaga);
  yield takeEvery('VERIFY_RESET_TOKEN_SAGA', verifyResetTokenSaga);
  yield takeEvery('RESET_PASSWORD_SAGA', resetPasswordSaga);
}

export default authSaga;